import { useState, useEffect } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { Dialog, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PromptLibrary } from "@/types/models";
import { useAdminTemplateTypes, useAdminTemplateFormatTypes } from "@/hooks/useMasterData";
import { cn } from "@/lib/utils";

interface PromptLibraryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultValues?: Partial<PromptLibrary>;
  onSubmit: (values: Partial<PromptLibrary>) => Promise<void>;
  isSubmitting: boolean;
  templateId?: string; // For context only - association is managed separately
}

export function PromptLibraryFormDialog({
  open,
  onOpenChange,
  defaultValues,
  onSubmit,
  isSubmitting,
}: PromptLibraryFormDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [priming, setPriming] = useState("");
  const [templateTypeId, setTemplateTypeId] = useState<number | null>(null);
  const [templateTypeError, setTemplateTypeError] = useState(false);
  const [formattypeIds, setFormattypeIds] = useState<number[]>([]);
  const [formatTypesError, setFormatTypesError] = useState(false);
  const [formatTypesOpen, setFormatTypesOpen] = useState(false);

  const { data: allTemplateTypes = [] } = useAdminTemplateTypes();
  const { data: allFormatTypes = [] } = useAdminTemplateFormatTypes();
  const activeTemplateTypes = allTemplateTypes.filter((t) => t.is_active);
  const activeFormatTypes = allFormatTypes.filter((t) => t.is_active);

  useEffect(() => {
    if (open) {
      setName(defaultValues?.name || "");
      setDescription(defaultValues?.description || "");
      setPriming(defaultValues?.priming || "");
      setTemplateTypeId(defaultValues?.template_type_id ?? null);
      setFormattypeIds(defaultValues?.template_formatetype_ids ?? []);
      setTemplateTypeError(false);
      setFormatTypesError(false);
    }
  }, [open, defaultValues]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (templateTypeId == null) {
      setTemplateTypeError(true);
      return;
    }
    if (formattypeIds.length === 0) {
      setFormatTypesError(true);
      return;
    }

    const formData = {
      name: name.trim(),
      description: description.trim() || null,
      priming: priming.trim() || null,
      // Default values (not shown in UI)
      model: "GPT-5.2",
      temperature: 1.0,
      is_active: true,
      image: null,
      config: null,
      template_type_id: templateTypeId,
      template_formatetype_ids: formattypeIds,
    };

    await onSubmit(formData);

    // Reset form
    setName("");
    setDescription("");
    setPriming("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-[60] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-[60] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg"
          )}
        >
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>
                {defaultValues ? "Edit Prompt" : "Add Prompt"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="font-semibold">
                  Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter prompt library name"
                  disabled={isSubmitting}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter boilerplate section description shown to outline drafters (optional)"
                  disabled={isSubmitting}
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="priming">Priming (System Prompt)</Label>
                <Textarea
                  id="priming"
                  value={priming}
                  onChange={(e) => setPriming(e.target.value)}
                  placeholder="Enter system prompt or priming instructions (optional)"
                  disabled={isSubmitting}
                  rows={6}
                />
              </div>

              <div className="space-y-2">
                <Label>Template Type <span className="text-destructive">*</span></Label>
                <Select
                  value={templateTypeId != null ? String(templateTypeId) : ""}
                  onValueChange={(v) => { setTemplateTypeId(Number(v)); setTemplateTypeError(false); }}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className={templateTypeError ? "border-destructive" : ""}>
                    <SelectValue placeholder="Select template type..." />
                  </SelectTrigger>
                  <SelectContent className="z-[70]">
                    {activeTemplateTypes.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-muted-foreground">
                        No template types found. Add them in Admin → Template Types.
                      </div>
                    ) : (
                      activeTemplateTypes.map((t) => (
                        <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {templateTypeError && <p className="text-xs text-destructive">Template type is required.</p>}
              </div>

              <div className="space-y-2">
                <Label>Format Types <span className="text-destructive">*</span></Label>
                <Popover open={formatTypesOpen} onOpenChange={setFormatTypesOpen}>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" className={cn("w-full justify-start font-normal", formatTypesError ? "border-destructive" : "")} disabled={isSubmitting}>
                      {formattypeIds.length > 0 ? `${formattypeIds.length} selected` : "Select format types..."}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-1 z-[70]" align="start">
                    {activeFormatTypes.length === 0 && (
                      <p className="text-xs text-muted-foreground px-2 py-1.5">No format types available.</p>
                    )}
                    {activeFormatTypes.map((ft) => {
                      const checked = formattypeIds.includes(ft.id);
                      return (
                        <div
                          key={ft.id}
                          className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer"
                          onClick={() => { setFormattypeIds(checked ? formattypeIds.filter((id) => id !== ft.id) : [...formattypeIds, ft.id]); setFormatTypesError(false); }}
                        >
                          <Checkbox checked={checked} onCheckedChange={() => {}} />
                          <span className="text-sm">{ft.name}</span>
                        </div>
                      );
                    })}
                  </PopoverContent>
                </Popover>
                {formatTypesError && <p className="text-xs text-destructive">At least one format type is required.</p>}
                {formattypeIds.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {formattypeIds.map((id) => {
                      const ft = activeFormatTypes.find((t) => t.id === id);
                      return ft ? (
                        <Badge key={id} variant="secondary" className="gap-1">
                          {ft.name}
                          <button
                            type="button"
                            onClick={() => setFormattypeIds(formattypeIds.filter((v) => v !== id))}
                            className="ml-0.5 rounded-full hover:bg-muted-foreground/20"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || !name.trim() || templateTypeId == null || formattypeIds.length === 0} className="bg-brand-teal">
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity data-[state=open]:bg-accent data-[state=open]:text-muted-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </Dialog>
  );
}
